/**
 * Display state for the "Matches right now" panel: rows sorted biggest-first
 * via the SHARED default-order comparator (what an INHERIT container will
 * consume first), client-side name filter, show-all expansion past the cell
 * cap, and whether the catalog has lead counts worth showing.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useMemo, useState } from 'react';
import { compareListsByDefaultOrder } from '@trafficpulse/shared';

import type { ListGroupResolve, ListGroupResolveRow } from '../../../list-groups.types';

export const MATCHES_ROW_CAP = 24;

export interface MatchesDisplay {
  query: string;
  setQuery: (query: string) => void;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  /** Cells to render, after sort + filter + cap. */
  visible: ListGroupResolveRow[];
  /** Matches hidden behind the cap (0 while filtering or expanded). */
  hiddenCount: number;
  filteredCount: number;
  hasLeads: boolean;
  /** The grid is tall enough to need its own scrollbar. */
  scrolls: boolean;
}

export function useMatchesDisplay(resolve: ListGroupResolve | undefined): MatchesDisplay {
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(false);
  const rows = resolve?.lists;
  const needle = query.trim().toLowerCase();

  const sorted = useMemo(
    () =>
      [...(rows ?? [])].sort((a, b) =>
        compareListsByDefaultOrder(
          { wiserId: a.wiserId, name: a.name, leadCount: a.leadCount },
          { wiserId: b.wiserId, name: b.name, leadCount: b.leadCount },
        ),
      ),
    [rows],
  );
  const filtered = useMemo(
    () =>
      needle
        ? sorted.filter((row) => (row.name ?? `#${row.wiserId}`).toLowerCase().includes(needle))
        : sorted,
    [sorted, needle],
  );

  const showAll = expanded || needle.length > 0;
  const visible = showAll ? filtered : filtered.slice(0, MATCHES_ROW_CAP);
  return {
    query,
    setQuery,
    expanded,
    setExpanded,
    visible,
    hiddenCount: filtered.length - visible.length,
    filteredCount: filtered.length,
    hasLeads: sorted.some((row) => row.leadCount != null),
    scrolls: showAll && filtered.length > MATCHES_ROW_CAP,
  };
}
